import React, { useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone, MapPin } from "lucide-react";
import { useLang } from "../../i18n/LanguageProvider";
import { AGENCY } from "../../i18n/dict";
import { scrollToId } from "./SmoothScroll";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG =
  "https://images.unsplash.com/photo-1768365791069-bac100684ed5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxkcmFtYXRpYyUyMG1vdW50YWluJTIwbGFrZSUyMGFlcmlhbHxlbnwwfHx8fDE3ODQyNDMyNDB8MA&ixlib=rb-4.1.0&q=85";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: {
      duration: 1.15,
      delay: 0.3 + i * 0.12,
      ease: [0.76, 0, 0.24, 1],
    },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 0.9 + i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const { t } = useLang();
  const heroRef = useRef(null);
  const navigate = useNavigate();

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
  const veilOpacity = useTransform(scrollYProgress, [0, 1], [1, 1.4]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  // form state
  const [form, setForm] = useState({ name: "", email: "", phone: "", destination: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name & email required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "hero_form" });
      toast.success(t.hero.success);
      setForm({ name: "", email: "", phone: "", destination: "", message: "" });
    } catch (err) {
      toast.error(t.hero.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="top"
      ref={heroRef}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#0B1416] text-[#F5F1E8]"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={HERO_IMG}
          alt="Voyage aérien"
          className="w-full h-[115%] object-cover"
          loading="eager"
        />
      </motion.div>

      {/* Veil */}
      <motion.div style={{ opacity: veilOpacity }} className="absolute inset-0 hero-veil grain" />

      {/* Top meta strip */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={0}
        className="absolute top-24 left-6 md:left-10 lg:left-16 right-6 md:right-10 lg:right-16 flex items-center justify-between text-[11px] uppercase tracking-[0.32em] z-10"
      >
        <span className="opacity-80">— {t.hero.eyebrow}</span>
        <span className="hidden sm:flex items-center gap-2 opacity-80">
          <MapPin size={12} /> 41.5°N · 2.4°E
        </span>
      </motion.div>

      {/* Main content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pt-40 md:pt-48 pb-24 min-h-screen flex flex-col justify-between"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end flex-1">
          {/* Kinetic reveal headline */}
          <div className="lg:col-span-7 flex flex-col justify-end pt-16 lg:pt-0">
            <h1
              data-testid="hero-title"
              className="font-heading-tight text-[15vw] md:text-[10vw] lg:text-[9vw] leading-[0.86]"
              style={{ letterSpacing: "-0.045em" }}
            >
              {t.hero.title_lines.map((line, i) => (
                <span key={i} className="mask-line">
                  <motion.span
                    variants={lineReveal}
                    initial="hidden"
                    animate="show"
                    custom={i}
                    className="inline-block"
                  >
                    {i === 1 ? (
                      <span className="font-italic text-[#6EE3E6]">{line}</span>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1}
              className="max-w-xl mt-8 text-[15px] md:text-[17px] font-light leading-relaxed opacity-80"
            >
              {t.hero.lede}
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={2}
              className="flex flex-wrap gap-3 mt-10"
            >
              <button
                data-testid="hero-cta-book"
                onClick={() => navigate("/book")}
                className="tt-btn tt-btn-primary"
              >
                {t.hero.book_now}
                <ArrowUpRight size={14} />
              </button>
              <button
                data-testid="hero-cta-quote"
                onClick={() => scrollToId("booking")}
                className="tt-btn tt-btn-ghost"
              >
                {t.hero.quote}
              </button>
              <a
                data-testid="hero-cta-call"
                href={`tel:${AGENCY.phone_raw}`}
                className="tt-btn tt-btn-ghost"
              >
                <Phone size={12} />
                {AGENCY.phone}
              </a>
            </motion.div>
          </div>

          {/* Floating glass form */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <form
              data-testid="hero-contact-form"
              onSubmit={handleSubmit}
              className="relative backdrop-blur-2xl bg-[#0B1416]/40 border border-[#F5F1E8]/15 p-8 md:p-10 rounded-sm"
            >
              <div className="absolute -top-3 left-8 bg-[#1BB6B9] text-[#0B1416] px-3 py-1 text-[10px] tracking-[0.28em] uppercase font-medium">
                24h
              </div>
              <div className="font-heading text-3xl md:text-4xl leading-tight">
                {t.hero.form_title}
              </div>
              <div className="text-xs opacity-60 mt-2 tracking-wide">
                {t.hero.form_subtitle}
              </div>

              <div className="mt-8 space-y-2">
                <input
                  data-testid="hero-form-name"
                  className="tt-input"
                  name="name"
                  placeholder={t.hero.name}
                  value={form.name}
                  onChange={handleChange}
                  required
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    data-testid="hero-form-email"
                    className="tt-input"
                    name="email"
                    type="email"
                    placeholder={t.hero.email}
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                  <input
                    data-testid="hero-form-phone"
                    className="tt-input"
                    name="phone"
                    placeholder={t.hero.phone}
                    value={form.phone}
                    onChange={handleChange}
                  />
                </div>
                <input
                  data-testid="hero-form-destination"
                  className="tt-input"
                  name="destination"
                  placeholder={t.hero.destination}
                  value={form.destination}
                  onChange={handleChange}
                />
                <textarea
                  data-testid="hero-form-message"
                  className="tt-input resize-none"
                  name="message"
                  rows={3}
                  placeholder={t.hero.message}
                  value={form.message}
                  onChange={handleChange}
                />
              </div>

              <button
                data-testid="hero-form-submit"
                type="submit"
                disabled={submitting}
                className="tt-btn tt-btn-primary mt-8 w-full justify-center disabled:opacity-60"
              >
                {submitting ? t.hero.submitting : t.hero.submit}
                <ArrowUpRight size={14} />
              </button>
            </form>
          </motion.div>
        </div>

        {/* bottom scroll cue */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={3}
          className="hidden md:flex items-center justify-between mt-12 text-[11px] uppercase tracking-[0.32em] opacity-70"
        >
          <span>Scroll</span>
          <span>Travel To Time · Est. 2011</span>
        </motion.div>
      </motion.div>
    </section>
  );
};
